import {format} from 'date-fns'

import {businessCalendarDate} from 'src/api/business-time'

import {formatJobTime} from '../jobs/job-schedule-format'
import {
    boardAssignmentsForResourceStrip,
    boardJobScheduleDateRange,
} from './dispatch-board-resource-strip'
import {compareDispatchSchedules} from './dispatch-schedule-interval'
import type {
    DispatchAssignment,
    DispatchJobSchedule,
    DispatchTimelineData,
    UnassignedJob,
} from './types'

function fallbackSchedule(
    job: Pick<UnassignedJob, 'span' | 'scheduleKind'>,
    data: DispatchTimelineData,
): DispatchJobSchedule | null {
    const range = boardJobScheduleDateRange(job as UnassignedJob, data)
    if (range == null) {
        return null
    }
    return {
        startDate: range.startDate,
        startTime: null,
        endDate: range.endDate,
        endTime: null,
    }
}

function scheduleForJob(
    job: Pick<UnassignedJob, 'span' | 'scheduleKind' | 'schedule'>,
    data: DispatchTimelineData,
): DispatchJobSchedule | null {
    return job.schedule ?? fallbackSchedule(job, data)
}

function assignmentUsesResource(assignment: DispatchAssignment, resourceId: string): boolean {
    return (
        assignment.resourceId === resourceId ||
        assignment.allocations?.some((allocation) => allocation.resourceId === resourceId) === true
    )
}

function boardBookingsForResource(
    resourceId: string,
    data: DispatchTimelineData,
): Array<{
    jobId: string
    jobName: string
    schedule: DispatchJobSchedule | null
}> {
    const bookings = data.unassignedJobs.flatMap((job) =>
        job.allocations.some((allocation) => allocation.resourceId === resourceId)
            ? [{jobId: job.id, jobName: job.label, schedule: scheduleForJob(job, data)}]
            : [],
    )
    const knownJobIds = new Set(bookings.map((booking) => booking.jobId))

    for (const assignment of data.assignments) {
        const jobId = assignment.sourceJobId ?? assignment.id
        if (knownJobIds.has(jobId) || !assignmentUsesResource(assignment, resourceId)) {
            continue
        }
        bookings.push({
            jobId,
            jobName: assignment.label,
            schedule: scheduleForJob(assignment, data),
        })
    }

    return bookings
}

export type OverlappingBoardBooking = {
    jobId: string
    jobName: string
    mode: 'timed' | 'calendar'
    release?: {date: string; time: string}
    schedule?: DispatchJobSchedule
}

export function bookingAvailabilityMessage(booking: OverlappingBoardBooking): string {
    if (booking.release != null) {
        const releaseDate = new Date(`${booking.release.date}T00:00:00`)
        const dateLabel =
            format(releaseDate, 'yyyy-MM-dd') === format(businessCalendarDate(), 'yyyy-MM-dd')
                ? 'today'
                : `on ${format(releaseDate, 'MMM d')}`
        return `Unavailable until ${formatJobTime(booking.release.time)} ${dateLabel}`
    }

    if (booking.schedule?.endDate == null) {
        return 'Unavailable until it is returned or released'
    }

    if (booking.schedule.endDate !== booking.schedule.startDate) {
        return 'Unavailable for the scheduled day because the booking spans multiple days'
    }

    return 'Unavailable for the scheduled day because the end time is unknown'
}

/** Board bookings for a resource that overlap the draft job schedule. */
export function overlappingBoardBookings(
    resourceId: string,
    draftJob: UnassignedJob,
    data: DispatchTimelineData,
): OverlappingBoardBooking[] {
    const draftSchedule = scheduleForJob(draftJob, data)
    if (draftSchedule == null) {
        return []
    }

    const bookings = boardBookingsForResource(resourceId, data)
        .filter((booking) => booking.jobId !== draftJob.id && booking.schedule != null)
        .flatMap((booking) => {
            const comparison = compareDispatchSchedules(draftSchedule, booking.schedule!)
            return comparison.overlaps
                ? [
                      {
                          jobId: booking.jobId,
                          jobName: booking.jobName,
                          mode: comparison.mode,
                          release: comparison.mode === 'timed' ? comparison.release : undefined,
                          schedule: booking.schedule!,
                      },
                  ]
                : []
        })

    // Unknown release times control the explanation. Otherwise the latest known
    // timed release is the one that keeps the resource unavailable longest.
    return bookings.sort((left, right) => {
        if (left.release == null && right.release != null) return -1
        if (left.release != null && right.release == null) return 1
        if (left.release == null || right.release == null) return 0
        return `${right.release.date}T${right.release.time}`.localeCompare(
            `${left.release.date}T${left.release.time}`,
        )
    })
}

/**
 * True when the resource is already on another board job whose calendar days
 * overlap the draft job. Uses the same board→strip rows as the week availability
 * bars so a grey day on the job cannot disagree with picker eligibility.
 */
export function resourceHasScheduleConflict(
    resourceId: string,
    draftJob: UnassignedJob,
    data: DispatchTimelineData,
): boolean {
    return overlappingBoardBookings(resourceId, draftJob, data).length > 0
}

/** True when the resource has any board booking besides the draft job. */
export function resourceHasOtherBoardBookings(
    resourceId: string,
    draftJobId: string,
    data: DispatchTimelineData,
): boolean {
    return boardAssignmentsForResourceStrip(data, resourceId).some(
        (row) => row.jobId !== draftJobId,
    )
}
